import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Flame, Beef, Wheat, Droplets, Activity } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useLang } from "@/lib/LanguageContext";
import { recipeAPI, historyAPI } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";

export default function Home() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState([]);
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);

  useEffect(() => {
    recipeAPI.popularIngredients(lang).then((r) => setIngredients(r.data)).catch(() => {});
    if (user) {
      setLoadingRecipes(true);
      historyAPI.list()
        .then((r) => setRecentRecipes(r.data.slice(0, 10)))
        .catch(() => {})
        .finally(() => setLoadingRecipes(false));
    }
  }, [user, lang]);

  const handleIngredientClick = (name) => {
    if (user) {
      navigate("/generate", { state: { ingredient: name } });
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/lx59ppr3_image.png"
            alt="Couple cooking"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C0A09]/95 via-[#0C0A09]/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white leading-[1.1] mb-6">
              {t("heroTitle")}
            </h1>
            <p className="text-lg text-stone-400 mb-8 leading-relaxed">
              {t("heroSubtitle")}
            </p>
            <button
              onClick={() => navigate(user ? "/generate" : "/register")}
              data-testid="get-started-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-stone-950 font-semibold rounded-full hover:bg-amber-600 transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/20"
            >
              <Sparkles className="w-5 h-5" />
              {t("getStarted")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Nutrition Showcase - 2 Dish Photos */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="nutrition-showcase">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-amber-500 mb-3">
            {t("nutritionInfo")}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-3">
            {"Chaque recette, analys\u00e9e pour vous"}
          </h2>
          <p className="text-stone-500 max-w-lg mx-auto">
            {"Calories, prot\u00e9ines, glucides, lipides \u2014 toutes les infos nutritionnelles d\u00e9taill\u00e9es pour chaque recette g\u00e9n\u00e9r\u00e9e par l\u2019IA"}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Dish Card 1 */}
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl overflow-hidden group">
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/sn9j123d_Screenshot_20260417_231608_Qu%27est-ce%20qu%27on%20mange.jpg"
                alt="Poulet r\u00f4ti aux herbes"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09]/90 via-[#0C0A09]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-heading text-lg font-medium text-white mb-1">{"Poulet r\u00f4ti aux herbes"}</h3>
                <p className="text-xs text-stone-400 mb-3">{"Cuisson douce, saveurs intenses"}</p>
                <div className="flex flex-wrap gap-2">
                  <NutriBadge icon={<Flame className="w-3 h-3" />} value="380" unit="kcal" color="text-orange-400 bg-orange-500/15" />
                  <NutriBadge icon={<Beef className="w-3 h-3" />} value="42g" unit="prot." color="text-red-400 bg-red-500/15" />
                  <NutriBadge icon={<Wheat className="w-3 h-3" />} value="12g" unit="gluc." color="text-amber-400 bg-amber-500/15" />
                  <NutriBadge icon={<Droplets className="w-3 h-3" />} value="18g" unit="lip." color="text-yellow-400 bg-yellow-500/15" />
                </div>
              </div>
              {/* Health Score Badge */}
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 text-xs font-semibold px-2.5 py-1 rounded-full">
                <Activity className="w-3 h-3" />
                8/10
              </div>
            </div>
          </div>
          {/* Dish Card 2 */}
          <div className="bg-[#1C1917] border border-white/5 rounded-2xl overflow-hidden group">
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_flavor-forge-68/artifacts/b1mayobo_Screenshot_20260417_231905_Qu%27est-ce%20qu%27on%20mange.jpg"
                alt="B\u0153uf bourguignon traditionnel"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09]/90 via-[#0C0A09]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-heading text-lg font-medium text-white mb-1">{"B\u0153uf bourguignon traditionnel"}</h3>
                <p className="text-xs text-stone-400 mb-3">{"Mijot\u00e9 au vin rouge, fondant"}</p>
                <div className="flex flex-wrap gap-2">
                  <NutriBadge icon={<Flame className="w-3 h-3" />} value="420" unit="kcal" color="text-orange-400 bg-orange-500/15" />
                  <NutriBadge icon={<Beef className="w-3 h-3" />} value="38g" unit="prot." color="text-red-400 bg-red-500/15" />
                  <NutriBadge icon={<Wheat className="w-3 h-3" />} value="15g" unit="gluc." color="text-amber-400 bg-amber-500/15" />
                  <NutriBadge icon={<Droplets className="w-3 h-3" />} value="22g" unit="lip." color="text-yellow-400 bg-yellow-500/15" />
                </div>
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-green-500/20 backdrop-blur-sm border border-green-500/30 text-green-400 text-xs font-semibold px-2.5 py-1 rounded-full">
                <Activity className="w-3 h-3" />
                7/10
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-8">
          <button
            onClick={() => navigate(user ? "/generate" : "/register")}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-amber-500/30 text-amber-500 rounded-full text-sm font-medium hover:bg-amber-500/10 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Essayer maintenant
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Popular Ingredients */}
      {ingredients.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-white mb-8">
            {t("popularIngredients")}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {ingredients.map((ing) => (
              <button
                key={ing.name}
                onClick={() => handleIngredientClick(ing.name)}
                data-testid={`ingredient-${ing.name}`}
                className="flex items-center gap-3 px-4 py-3 bg-[#1C1917] border border-white/5 rounded-xl hover:border-amber-500/30 hover:bg-amber-500/5 transition-all duration-200 group"
              >
                <span className="text-2xl">{ing.emoji}</span>
                <span className="text-sm text-stone-300 group-hover:text-white capitalize transition-colors">
                  {ing.name}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Recent Recipes Carousel */}
      {user && recentRecipes.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-white">
              {t("recentRecipes")}
            </h2>
            <button
              onClick={() => navigate("/history")}
              className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
            >
              {t("history")} &rarr;
            </button>
          </div>
          <div
            className="flex overflow-x-auto gap-5 snap-x snap-mandatory pb-4 hide-scrollbar"
            data-testid="recipe-carousel"
          >
            {recentRecipes.map((item) => (
              <div key={item.id} className="snap-center flex-shrink-0 w-64 sm:w-72">
                <RecipeCard
                  recipe={item.recipe}
                  onClick={() => navigate("/recipe", { state: { recipe: item.recipe } })}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {loadingRecipes && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex gap-5 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-64 sm:w-72 bg-[#1C1917] rounded-2xl animate-pulse">
                <div className="aspect-[4/3] bg-stone-800 rounded-t-2xl" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-stone-800 rounded w-3/4" />
                  <div className="h-3 bg-stone-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function NutriBadge({ icon, value, unit, color }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${color}`}>
      {icon}
      {value} {unit}
    </span>
  );
}
